import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  CheckCircle2, Loader2, RotateCcw, FileSignature, AlertTriangle, Download,
} from "lucide-react";
import { toast } from "sonner";
import { maskCPF, validaCPF } from "../lib/validators";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TIPO_LABEL = { locacao: "Locação", venda: "Venda", servico: "Serviço", outros: "Comercial" };

const fmtMoney = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function AssinarProposta() {
  const { token } = useParams();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nome, setNome] = useState("");
  const [cpf, setCpf] = useState("");
  const [assinando, setAssinando] = useState(false);
  const [assinado, setAssinado] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND}/api/publico/proposta-nativa/${token}`)
      .then((r) => setProp(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Proposta não encontrada ou link expirado"))
      .finally(() => setLoading(false));
  }, [token]);

  const getPos = (e) => {
    const c = canvasRef.current; if (!c) return null;
    const r = c.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - r.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - r.top;
    return { x: x * (c.width / r.width), y: y * (c.height / r.height) };
  };
  const startDraw = (e) => { e.preventDefault(); drawing.current = true; lastPt.current = getPos(e); };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pt = getPos(e);
    if (!pt || !lastPt.current) return;
    ctx.strokeStyle = "#1B3A6B";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(pt.x, pt.y);
    ctx.stroke();
    lastPt.current = pt;
  };
  const stopDraw = () => { drawing.current = false; lastPt.current = null; };
  const limparCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
  };

  const baixarPDF = () => {
    window.open(`${BACKEND}/api/publico/proposta-nativa/${token}/pdf`, "_blank");
  };

  const assinar = async () => {
    if (!nome.trim() || nome.trim().length < 3) {
      toast.error("Informe seu nome completo"); return;
    }
    if (!validaCPF(cpf)) {
      toast.error("CPF inválido — verifique o número digitado"); return;
    }
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const data = ctx.getImageData(0, 0, c.width, c.height).data;
    let hasInk = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 0) { hasInk = true; break; }
    }
    if (!hasInk) { toast.error("Assine no quadro acima"); return; }
    const b64 = c.toDataURL("image/png");
    setAssinando(true);
    try {
      await axios.post(`${BACKEND}/api/publico/proposta-nativa/${token}/assinar`, {
        nome: nome.trim(),
        cpf: cpf,
        assinatura_base64: b64,
      });
      setAssinado(true);
      toast.success("Proposta assinada com sucesso!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao assinar");
    } finally { setAssinando(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <Loader2 size={32} className="animate-spin" style={{ color: "#1B3A6B" }} />
    </div>;
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-zinc-100">
        <Card className="p-8 max-w-md text-center" data-testid="assinar-proposta-error">
          <AlertTriangle size={48} className="mx-auto mb-3 text-red-500" />
          <h1 className="text-xl font-bold mb-2">Link não disponível</h1>
          <p className="text-sm text-muted-foreground">{error}</p>
        </Card>
      </div>
    );
  }

  if (assinado || prop?.status === "assinada") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-emerald-50">
        <Card className="p-8 max-w-md text-center" data-testid="assinar-proposta-sucesso">
          <CheckCircle2 size={64} className="mx-auto mb-3 text-green-600" />
          <h1 className="text-2xl font-bold mb-2" style={{ color: "#1B3A6B" }}>Proposta assinada!</h1>
          <p className="text-sm text-muted-foreground mb-4">
            Recebemos sua assinatura. Nossa equipe entrará em contato em breve para concluir o processo.
          </p>
          <Button variant="outline" onClick={baixarPDF} data-testid="baixar-pdf-final">
            <Download size={14} className="mr-2" /> Baixar proposta em PDF
          </Button>
          <p className="text-xs text-muted-foreground mt-4">Locar Ativo Brasil — Obrigado pela parceria!</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 py-8 px-4" data-testid="assinar-proposta-page">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Header */}
        <Card className="p-6" style={{ background: "#0A2540", color: "white" }}>
          <div className="text-xs font-bold tracking-widest opacity-70 mb-1">
            PROPOSTA Nº {prop.numero}
          </div>
          <h1 className="text-2xl font-display font-bold mb-1">
            <FileSignature size={28} className="inline mr-2" />
            {TIPO_LABEL[prop.tipo_contrato] || "Proposta Comercial"}
          </h1>
          <p className="text-sm opacity-80">
            Locar Ativo Brasil · Olá, <strong>{prop.cliente_nome}</strong>!
          </p>
        </Card>

        {/* Resumo */}
        <Card className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="font-bold" style={{ color: "#1B3A6B" }}>Resumo da proposta</h2>
            <Button size="sm" variant="outline" onClick={baixarPDF} data-testid="baixar-pdf-btn">
              <Download size={14} className="mr-1" /> Baixar PDF completo
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm mb-4">
            <div><span className="text-muted-foreground">Cliente:</span> <strong>{prop.cliente_nome}</strong></div>
            <div><span className="text-muted-foreground">Documento:</span> <strong>{prop.cliente_documento || "—"}</strong></div>
            <div><span className="text-muted-foreground">Consultor:</span> <strong>{prop.vendedor_nome}</strong></div>
            <div><span className="text-muted-foreground">Itens:</span> <strong>{prop.itens?.length || 0}</strong></div>
          </div>
          <div className="border-t pt-3 space-y-1.5">
            {(prop.itens || []).map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <div>
                  <span className="font-medium">{it.nome}</span>
                  <span className="text-muted-foreground"> · {it.quantidade}× · {it.prazo_meses} meses</span>
                </div>
                <span className="font-mono font-bold" style={{ color: "#1B3A6B" }}>
                  {fmtMoney(it.valor_total || it.quantidade * it.valor_mensal * it.prazo_meses)}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t mt-3 pt-3 flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {prop.tipo_contrato === "locacao" ? "Valor total do contrato:" : "Valor total:"}
            </span>
            <span className="text-2xl font-display font-bold" style={{ color: "#F97316" }}>
              {fmtMoney(prop.valor_total_geral)}
            </span>
          </div>
        </Card>

        {/* Área de assinatura */}
        <Card className="p-6">
          <h2 className="font-bold mb-3" style={{ color: "#1B3A6B" }}>Assinatura digital</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Ao assinar abaixo, você declara estar de acordo com os equipamentos, valores e condições apresentadas nesta proposta.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <Label>Nome completo *</Label>
              <Input
                data-testid="assinar-nome"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Seu nome completo"
              />
            </div>
            <div>
              <Label>CPF *</Label>
              <Input
                data-testid="assinar-cpf"
                value={cpf}
                onChange={(e) => setCpf(maskCPF(e.target.value))}
                placeholder="000.000.000-00"
                maxLength={14}
              />
            </div>
          </div>

          <div>
            <Label>Assine abaixo *</Label>
            <div className="border-2 border-dashed rounded-lg p-1 bg-white mt-1">
              <canvas
                ref={canvasRef}
                data-testid="assinar-canvas"
                width={700}
                height={180}
                className="w-full touch-none cursor-crosshair"
                onMouseDown={startDraw}
                onMouseMove={draw}
                onMouseUp={stopDraw}
                onMouseLeave={stopDraw}
                onTouchStart={startDraw}
                onTouchMove={draw}
                onTouchEnd={stopDraw}
              />
            </div>
            <div className="flex justify-between items-center mt-2">
              <Button variant="ghost" size="sm" onClick={limparCanvas} data-testid="assinar-limpar">
                <RotateCcw size={14} className="mr-1" /> Limpar assinatura
              </Button>
              <span className="text-xs text-muted-foreground">Use o mouse ou o dedo para assinar</span>
            </div>
          </div>

          <Button
            data-testid="assinar-confirmar"
            onClick={assinar}
            disabled={assinando}
            className="w-full text-white mt-4 h-12 text-base"
            style={{ background: "#F97316" }}
          >
            {assinando ? <><Loader2 size={16} className="animate-spin mr-2" /> Enviando assinatura...</> : <>✍️ Assinar e Aprovar Proposta</>}
          </Button>
          <p className="text-xs text-muted-foreground text-center mt-2">
            🔒 Sua assinatura é registrada de forma segura e tem validade jurídica.
          </p>
        </Card>
      </div>
    </div>
  );
}
